import { useCallback, useEffect, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { onCaptureOrganizationSettled } from '../organize-client';
import { reportError } from '../../lib/monitoring';
import type {
  CaptureOrganizationRow,
  CaptureRow,
  CaptureTranscriptVersionRow,
} from '../database.types';

/**
 * One Capture and its Final Accepted Transcript. M3 / S-03-02.
 *
 * AUTHORIZATION IS RLS, NOT A CLIENT CHECK. `capture_select_own` only matches
 * rows the caller owns, so another user's Capture returns NO ROWS and is
 * reported as not-found. **Crewlink never says "that exists but is not yours"**,
 * because that sentence is itself a disclosure — the same rule `use-project.ts`
 * follows, for the same reason.
 *
 * The id in the URL therefore grants nothing.
 */

export type CaptureDetailStatus = 'loading' | 'ready' | 'not-found' | 'failed';

export interface CaptureDetailState {
  status: CaptureDetailStatus;
  capture: CaptureRow | null;
  /**
   * The CURRENT Final Accepted Transcript, or null when this Capture has none.
   *
   * **Read from `capture_transcript_version`, which is the authoritative
   * store.** `work_log_event.detail.transcript` holds a QUOTE for provenance
   * display and must never be read as the transcript (D-016) — a copy is
   * allowed to fall behind the thing it copied, and a memory product that
   * shows the stale one is showing a lie about what was said.
   *
   * **Current means the HIGHEST version, not version 1** (M6 / S06-03). Until
   * editing existed those were the same row, and this hook asked for version 1
   * by number. They are no longer the same row, and asking by number would
   * have shown Brandon his uncorrected words for as long as the app lived.
   */
  transcript: string | null;
  /**
   * The version number of the transcript above, or null when there is none.
   *
   * Two callers need it and neither can be served by the body alone: it is the
   * concurrency guard sent with an edit, and `> 1` is what makes the "Edited"
   * indicator true rather than decorative.
   */
  transcriptVersion: number | null;
  /**
   * EVERY accepted version, newest first — so `[0]` is the current one and the
   * rest are what "View previous version" shows.
   *
   * Fetched with the Capture rather than on demand. A transcript is a few
   * hundred bytes and a Capture has a handful of them at most; a second query,
   * a second loading state and a second refresh boundary would all be
   * machinery bought with nothing. If a Capture ever accumulates enough
   * versions for that to be false, the evidence will say so.
   */
  transcriptVersions: CaptureTranscriptVersionRow[];
  /**
   * What Automatic Organization derived, or null when it has never run for this
   * Capture (M7 / S07-03).
   *
   * **Null is an ordinary state, not a failure.** Every Capture saved before
   * S07-02 has no row, and Detail offers an explicit Organize for those rather
   * than starting one because a screen appeared — a render that spent a model
   * call would be a bill nobody asked for.
   */
  organization: CaptureOrganizationRow | null;
  error: string | null;
  reload: () => Promise<void>;
}

type FetchResult =
  | {
      kind: 'ready';
      row: CaptureRow;
      versions: CaptureTranscriptVersionRow[];
      organization: CaptureOrganizationRow | null;
    }
  | { kind: 'not-found' }
  | { kind: 'failed' };

export function useCapture(captureId: string | undefined): CaptureDetailState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<CaptureDetailStatus>(client ? 'loading' : 'failed');
  const [capture, setCapture] = useState<CaptureRow | null>(null);
  const [versions, setVersions] = useState<CaptureTranscriptVersionRow[]>([]);
  const [organization, setOrganization] = useState<CaptureOrganizationRow | null>(null);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  const fetchCapture = useCallback(async (): Promise<FetchResult> => {
    if (!client || !captureId) return { kind: 'not-found' };

    // maybeSingle(): zero rows is an ordinary outcome. It is what RLS returns
    // for someone else's Capture as well as for one that does not exist, and
    // those two must look identical from here.
    const { data, error: queryError } = await client
      .from('capture')
      .select('*')
      .eq('id', captureId)
      .maybeSingle();

    if (queryError) {
      reportError(queryError, 'capture.load');
      return { kind: 'failed' };
    }
    if (!data) return { kind: 'not-found' };

    // NEWEST FIRST, and the whole history in one query. The head is the
    // current transcript; the tail is what "View previous version" reveals.
    // `version desc` is a total order — `unique (capture_id, version)`
    // guarantees no tie — so unlike the Work Log this needs no tiebreaker.
    const { data: versionRows, error: versionError } = await client
      .from('capture_transcript_version')
      .select('*')
      .eq('capture_id', captureId)
      .order('version', { ascending: false });

    if (versionError) {
      reportError(versionError, 'capture.transcript');
      return { kind: 'failed' };
    }

    // ONE ROW AT MOST — `capture_id` is the primary key. **Reading it never
    // organizes anything**: no writer is called and no endpoint is reached, so
    // opening a Capture cannot spend a model call.
    const { data: organizationRow, error: organizationError } = await client
      .from('capture_organization')
      .select('*')
      .eq('capture_id', captureId)
      .maybeSingle();

    if (organizationError) {
      reportError(organizationError, 'capture.organization');
      return { kind: 'failed' };
    }

    // No rows is a valid Capture with no transcript, not a failure (PT §5).
    return {
      kind: 'ready',
      row: data,
      versions: versionRows ?? [],
      organization: organizationRow ?? null,
    };
  }, [client, captureId]);

  const applyResult = useCallback((result: FetchResult) => {
    if (result.kind === 'failed') {
      setError('Could not load this Capture. Check your connection and try again.');
      setStatus('failed');
      return;
    }
    if (result.kind === 'not-found') {
      setCapture(null);
      setVersions([]);
      setOrganization(null);
      setError(null);
      setStatus('not-found');
      return;
    }
    setCapture(result.row);
    setVersions(result.versions);
    setOrganization(result.organization);
    setError(null);
    setStatus('ready');
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchCapture().then((result) => {
      if (!cancelled) applyResult(result);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchCapture, applyResult]);

  const reload = useCallback(async () => {
    applyResult(await fetchCapture());
  }, [fetchCapture, applyResult]);

  /**
   * CATCH UP WHEN A BACKGROUND ORGANIZATION ATTEMPT FINISHES — M7 post-closeout.
   *
   * Organization is asked for AFTER a Capture saves and AFTER a transcript is
   * corrected, and it can take several seconds. Until this existed, a Detail
   * screen opened during that window kept its first snapshot forever: the
   * database said `organized` with a real location, and the title still read
   * "Location not set" until an unrelated mutation happened to reload. **The
   * only way Brandon could reveal an already-saved AI location was Edit →
   * Save, which reads as having to accept it by hand.**
   *
   * **THIS RE-READS AND NOTHING ELSE.** `fetchCapture` is three `.select()`
   * calls; no writer runs, no endpoint is reached, and no provider call can
   * originate here — so a refresh can never trigger a second reasoning
   * request, and it cannot make an AI-derived value human-owned.
   *
   * Scoped to THIS Capture by id, so another Capture organizing in the
   * background does not make this screen fetch for nothing.
   */
  useEffect(() => {
    if (!captureId) return;
    let cancelled = false;
    const unsubscribe = onCaptureOrganizationSettled((settledCaptureId) => {
      if (settledCaptureId !== captureId) return;
      void fetchCapture().then((result) => {
        // The screen may have been left while the read was in flight; a late
        // response must not update a Capture nobody is looking at.
        if (!cancelled) applyResult(result);
      });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [captureId, fetchCapture, applyResult]);

  // The current version is the head of a list that is already sorted, so
  // "current" and "previous" cannot disagree about which row is which.
  const current = versions[0] ?? null;

  return {
    status,
    capture,
    transcript: current?.body ?? null,
    transcriptVersion: current?.version ?? null,
    transcriptVersions: versions,
    organization,
    error,
    reload,
  };
}
